/**
 * Zentrale API-Konfiguration für das Frontend
 * Ermittelt die Backend-URL dynamisch basierend auf der Server-Konfiguration aus der Datenbank
 */

import { getAuthHeaders } from './authUtils';

let serverConfig = null;
let apiBaseUrl = null;

// Funktion zur dynamischen Ermittlung der Backend URL
const getInitialApiBaseUrl = () => {
  // Prüfe zuerst Umgebungsvariable
  if (process.env.REACT_APP_API_URL) {
    let url = process.env.REACT_APP_API_URL;
    // HTTPS erzwingen wenn Seite über HTTPS geladen wurde
    if (window.location.protocol === 'https:' && url.startsWith('http://')) {
      url = url.replace('http://', 'https://');
      console.log('[apiConfig] Fixed REACT_APP_API_URL: http:// -> https://');
    }
    return url;
  }
  
  // Fallback: Verwende die aktuelle Host-Adresse mit Standard-Port
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  
  // In Entwicklungsumgebung: localhost mit Backend-Port
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:25000`;
  }
  
  // Produktionsumgebung: Erkenne bekannte Domains und verwende API-Subdomain
  // Format: reporting.example.com -> api.reporting.example.com
  if (hostname.includes('reporting.steffen-tietz.de')) {
    return `${protocol}//api.reporting.steffen-tietz.de`;
  }
  
  // Generische Produktionsumgebung: Wenn hostname mit 'reporting.' beginnt, 
  // ersetze durch 'api.reporting.'
  if (hostname.startsWith('reporting.')) {
    return `${protocol}//api.${hostname}`;
  }
  
  // Fallback: gleicher Host, kein Port (Nginx proxy)
  return `${protocol}//${hostname}`;
};

// Funktion um Server-Konfiguration zu laden und API-URL zu aktualisieren
export const loadServerConfig = async (customAuthHeaders = {}) => {
  try {
    // Verwende zunächst die Standard-URL um die Konfiguration zu laden
    const initialUrl = getInitialApiBaseUrl();
    const authHeaders = getAuthHeaders();
    const response = await fetch(`${initialUrl}/admin/config/Server`, {
      headers: {
        ...authHeaders,
        ...customAuthHeaders
      }
    });
    
    if (response.ok) {
      const config = await response.json();
      serverConfig = {
        server_listen_ip: config.listen_ip || '127.0.0.1',
        server_listen_port: config.listen_port || '25000',
        server_aet_title: config.aet_title || 'REPORTING_SCP',
        use_approval_workflow: config.use_approval_workflow
      };
      
      // Aktualisiere die API Base URL basierend auf der geladenen Konfiguration
      // In Produktion: Verwende die aktuelle Domain, nicht die Backend-IP
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      
      // Nur in Entwicklung die Server-IP verwenden
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        apiBaseUrl = `${protocol}//${serverConfig.server_listen_ip}:${serverConfig.server_listen_port}`;
      }
      // In Produktion bleibt die initialeURL (ohne Port) bestehen
      
      return serverConfig;
    } else if (response.status === 403) {
      // 403 Forbidden ist für Nicht-Admin-Benutzer normal - still fallback verwenden
      console.info('Server config not accessible (insufficient permissions), using fallback configuration');
    } else {
      console.warn('Failed to load server config:', response.status, response.statusText);
    }
  } catch (error) {
    console.warn('Could not load server config from config.ini, using fallback URL:', error);
  }
  
  // Fallback falls das Laden fehlschlägt
  apiBaseUrl = getInitialApiBaseUrl();
  return null;
};

// Hilfsfunktion um HTTPS zu erzwingen wenn nötig
const ensureHttps = (url) => {
  if (window.location.protocol === 'https:' && url && url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  return url;
};

// Funktion um die aktuelle API Base URL zu erhalten
export const getApiBaseUrl = () => {
  if (!apiBaseUrl) {
    apiBaseUrl = getInitialApiBaseUrl();
  }
  // IMMER HTTPS erzwingen wenn Seite über HTTPS geladen wurde
  return ensureHttps(apiBaseUrl);
};

// Funktion um die Server-Konfiguration zu erhalten
export const getServerConfig = () => {
  return serverConfig;
};

// Funktion um Server-Konfiguration manuell zu setzen (z.B. nach dem Speichern)
export const updateServerConfig = (newConfig) => {
  serverConfig = { ...newConfig };
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  
  // Nur in Entwicklung die Server-IP verwenden
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    apiBaseUrl = `${protocol}//${serverConfig.server_listen_ip}:${serverConfig.server_listen_port}`;
  }
  // In Produktion bleibt die aktuelle URL bestehen
};

// Export der Base URL (für Rückwärtskompatibilität)
// WICHTIG: Verwende Getter um immer die korrigierte URL zu erhalten
export const getAPI_BASE_URL = () => getApiBaseUrl();
// Legacy-Export - wird beim Import einmalig evaluiert, kann http:// enthalten
// Nutze stattdessen getApiBaseUrl() oder createApiUrl()
export const API_BASE_URL = getApiBaseUrl();

// Hilfsfunktion zum Erstellen vollständiger API URLs
export const createApiUrl = (endpoint) => {
  // getApiBaseUrl() erzwingt bereits HTTPS wenn nötig
  let baseUrl = getApiBaseUrl();
  
  // DEBUG: Zeige woher die URL kommt
  console.log('[apiConfig] createApiUrl DEBUG:', {
    endpoint,
    baseUrl,
    protocol: window.location.protocol,
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
    apiBaseUrlVar: apiBaseUrl
  });
  
  // FINALE SICHERUNG: Erzwinge HTTPS wenn Seite über HTTPS geladen
  if (window.location.protocol === 'https:' && baseUrl.startsWith('http://')) {
    console.warn('[apiConfig] FORCING HTTPS! Original was:', baseUrl);
    baseUrl = baseUrl.replace('http://', 'https://');
  }
  
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${baseUrl}/${cleanEndpoint}`;
};

// Hilfsfunktion für fetch mit automatischer URL-Erstellung und Session-Headers
export const apiFetch = (endpoint, options = {}) => {
  const url = createApiUrl(endpoint);
  const authHeaders = getAuthHeaders();
  
  // Für FormData-Uploads nicht Content-Type setzen (Browser macht das automatisch)
  const isFormData = options.body instanceof FormData;
  
  // Merge auth headers with existing headers
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...authHeaders,
    ...options.headers
  };
  
  return fetch(url, {
    ...options,
    headers
  });
};

// Debug-Funktion
export const logApiConfig = () => {
  console.log('API Configuration:', {
    baseUrl: getApiBaseUrl(),
    serverConfig: serverConfig,
    environment: process.env.NODE_ENV,
    customApiUrl: process.env.REACT_APP_API_URL
  });
}; 